import { type ReactNode, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Plus, Trash2 } from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';

import {
  changeFieldType,
  createEmptyField,
  FIELD_TYPE_LABEL,
  TYPE_HINT,
} from './fieldSchema';

function fieldNameError(
  name: string,
  reservedNames: readonly string[],
): string | undefined {
  if (name && !v4.isTemplatePathPropertyName(name)) {
    return 'Use letters, numbers, and underscores; start with a letter or underscore.';
  }
  if (reservedNames.includes(name)) {
    return `"${name}" is provided by the incoming email and cannot be used as an input name.`;
  }
  return undefined;
}

export function FieldListEditor({
  fields,
  inputTypes,
  onChange,
  title,
  description,
  addLabel = 'Add field',
  emptyMessage = 'No fields have been added yet.',
  readOnly = false,
  reservedNames = [],
}: {
  fields: v4.FieldSpec[];
  inputTypes: v4.FieldInputType[];
  onChange: (fields: v4.FieldSpec[]) => void;
  title?: string;
  description?: string;
  addLabel?: string;
  emptyMessage?: string;
  /** Renders the schema for inspection without exposing mutation controls. */
  readOnly?: boolean;
  /** Runtime-owned field names that cannot be declared in this field list. */
  reservedNames?: readonly string[];
}) {
  const updateFields = (next: v4.FieldSpec[]) => {
    if (!readOnly) {
      onChange(next);
    }
  };

  const set = (index: number, next: v4.FieldSpec) =>
    updateFields(
      fields.map((field, fieldIndex) => (fieldIndex === index ? next : field)),
    );

  return (
    <Stack spacing={1.5}>
      {title && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      )}

      {fields.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            borderStyle: 'dashed',
            bgcolor: 'background.default',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {emptyMessage}
          </Typography>
        </Paper>
      ) : (
        fields.map((field, index) => (
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }} key={index}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Field {index + 1}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Define what this field contains.
                </Typography>
              </Box>
              {!readOnly && (
                <IconButton
                  color="error"
                  size="small"
                  title="Remove field"
                  aria-label={`Remove field ${index + 1}`}
                  onClick={() =>
                    updateFields(
                      fields.filter((_, fieldIndex) => fieldIndex !== index),
                    )
                  }
                >
                  <Trash2 size={16} />
                </IconButton>
              )}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.5,
              }}
            >
              {/** Keep technical keys compatible with the template path grammar. */}
              <TextField
                size="small"
                fullWidth
                label="Field name"
                placeholder="e.g. shipmentReference"
                value={field.name}
                error={Boolean(fieldNameError(field.name, reservedNames))}
                helperText={fieldNameError(field.name, reservedNames)}
                onChange={
                  readOnly
                    ? undefined
                    : (event) =>
                        set(index, { ...field, name: event.target.value })
                }
                slotProps={{
                  input: { readOnly },
                }}
              />
              <TextField
                size="small"
                fullWidth
                label="Display label"
                placeholder="e.g. Shipment reference"
                value={field.label}
                onChange={
                  readOnly
                    ? undefined
                    : (event) =>
                        set(index, { ...field, label: event.target.value })
                }
                slotProps={{
                  input: { readOnly },
                }}
              />
              {readOnly ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Data type"
                  value={FIELD_TYPE_LABEL[field.inputType]}
                  helperText={TYPE_HINT[field.inputType]}
                  slotProps={{
                    input: { readOnly: true },
                  }}
                />
              ) : (
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Data type"
                  value={field.inputType}
                  helperText={TYPE_HINT[field.inputType]}
                  onChange={(event) =>
                    set(
                      index,
                      changeFieldType(
                        field,
                        event.target.value as v4.FieldInputType,
                      ),
                    )
                  }
                >
                  {inputTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {FIELD_TYPE_LABEL[type]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {readOnly ? (
                <Box sx={{ alignSelf: 'start', pt: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {field.optional ? 'Optional' : 'Required'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {field.optional
                      ? 'People can leave it blank.'
                      : 'People need to provide a value.'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ alignSelf: 'start', pt: 0.25 }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    label="This field is optional"
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(field.optional)}
                        onChange={(event) =>
                          set(index, {
                            ...field,
                            optional: event.target.checked,
                          })
                        }
                      />
                    }
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      ml: 4,
                    }}
                  >
                    People can leave it blank.
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1.25,
              }}
            >
              The field name is the technical key used in templates. The display
              label is shown to people.
            </Typography>

            <FieldTypeControl
              field={field}
              inputTypes={inputTypes}
              onChange={(next) => set(index, next)}
              readOnly={readOnly}
            />
          </Paper>
        ))
      )}

      {!readOnly && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={15} />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => updateFields([...fields, createEmptyField()])}
        >
          {addLabel}
        </Button>
      )}
    </Stack>
  );
}

function FieldConfiguration({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 1.25,
        }}
      >
        {description}
      </Typography>
      {children}
    </Box>
  );
}

/** Type-dependent affordance for a single field. */
function FieldTypeControl({
  field,
  inputTypes,
  onChange,
  readOnly = false,
}: {
  field: v4.FieldSpec;
  inputTypes: v4.FieldInputType[];
  onChange: (field: v4.FieldSpec) => void;
  readOnly?: boolean;
}) {
  if (field.inputType === 'select') {
    return (
      <FieldConfiguration
        title="Choices"
        description="Add the values someone can choose from."
      >
        <OptionsEditor
          options={field.options ?? []}
          onChange={(options) => onChange({ ...field, options })}
          readOnly={readOnly}
        />
      </FieldConfiguration>
    );
  }
  if (field.inputType === 'object') {
    return (
      <FieldConfiguration
        title="Fields inside this object"
        description="Define the individual pieces of information stored together."
      >
        <FieldListEditor
          title="Properties"
          fields={field.properties ?? []}
          inputTypes={inputTypes}
          onChange={(properties) => onChange({ ...field, properties })}
          addLabel="Add property"
          emptyMessage="No properties have been added yet."
          readOnly={readOnly}
        />
      </FieldConfiguration>
    );
  }
  if (field.inputType === 'array') {
    const item = field.items ?? {
      name: 'item',
      label: 'Item',
      inputType: 'text' as const,
    };
    return (
      <FieldConfiguration
        title="Items in this list"
        description="Choose what every item in the list contains."
      >
        {readOnly ? (
          <TextField
            size="small"
            fullWidth
            label="Each item is"
            value={FIELD_TYPE_LABEL[item.inputType]}
            slotProps={{
              input: { readOnly: true },
            }}
          />
        ) : (
          <TextField
            select
            size="small"
            fullWidth
            label="Each item is"
            value={item.inputType}
            onChange={(event) =>
              onChange({
                ...field,
                items: changeFieldType(
                  item,
                  event.target.value as v4.FieldInputType,
                ),
              })
            }
          >
            {inputTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {FIELD_TYPE_LABEL[type]}
              </MenuItem>
            ))}
          </TextField>
        )}
        <FieldTypeControl
          field={item}
          inputTypes={inputTypes}
          onChange={(nextItem) => onChange({ ...field, items: nextItem })}
          readOnly={readOnly}
        />
      </FieldConfiguration>
    );
  }
  return null;
}

/** Chip-based editor for a select field's options (serialises to string[]). */
function OptionsEditor({
  options,
  onChange,
  readOnly = false,
}: {
  options: string[];
  onChange: (options: string[]) => void;
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (value && !options.includes(value)) {
      onChange([...options, value]);
    }
    setDraft('');
  };

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {options.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No choices have been added yet.
          </Typography>
        )}
        {options.map((option) => (
          <Chip
            size="small"
            key={option}
            label={option}
            onDelete={
              readOnly
                ? undefined
                : () => onChange(options.filter((value) => value !== option))
            }
          />
        ))}
      </Box>
      {!readOnly && (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            label="Add a choice"
            placeholder="e.g. Approved"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                add();
              }
            }}
          />
          <Button variant="outlined" onClick={add}>
            Add
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
